import { Stack, SxProps, Theme } from '@mui/material';
import {
  ReactNode,
  UIEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ArrowDownIcon } from '@/components/icons/ArrowDownIcon';

const BOTTOM_SCROLL_THRESHOLD_PX = 8;

export interface ChatShellProps {
  emptyState: ReactNode;
  footer?: ReactNode;
  hasMessages: boolean;
  header?: ReactNode;
  /**
   * If true, pins the chat transcript to the bottom so that incoming messages
   * appear immediately. If the user scrolls away from the bottom, a jump to
   * bottom button appears in the lower-right corner of the chat window. The
   * pin behavior stops temporarily so that the user can review earlier
   * messages. Pinning resumes when the user scrolls to the bottom or selects
   * the jump to bottom button.
   *
   * This is currently used in no-document household onboarding.
   */
  pinTranscriptToBottom?: boolean;
  sx?: SxProps<Theme>;
  transcript: ReactNode;
}

/**
 * The chat column layout: header on top, the scrollable transcript (or empty
 * state) below it, and the footer pinned to the bottom. Hosts size the column
 * and render any companion surfaces (like a side content panel) beside it.
 */
export function ChatShell({
  emptyState,
  footer,
  hasMessages,
  header,
  pinTranscriptToBottom = true,
  sx,
  transcript,
}: ChatShellProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasUserScrollIntentRef = useRef(false);
  const isPinnedToBottomRef = useRef(true);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);

  const setPinnedToBottom = useCallback((isPinned: boolean) => {
    isPinnedToBottomRef.current = isPinned;
    setIsPinnedToBottom(isPinned);
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      setPinnedToBottom(true);
      scrollContainer.scrollTo({
        behavior,
        top: scrollContainer.scrollHeight,
      });
    },
    [setPinnedToBottom]
  );

  // Keep new and streaming content visible only while the transcript is pinned.
  // A new React node is produced whenever either chat's messages update.
  useLayoutEffect(() => {
    if (!pinTranscriptToBottom || !hasMessages) {
      setPinnedToBottom(true);
      return;
    }

    if (isPinnedToBottomRef.current) {
      scrollToBottom();
    }
  }, [
    hasMessages,
    pinTranscriptToBottom,
    scrollToBottom,
    setPinnedToBottom,
    transcript,
  ]);

  // The chat can mount inside a collapsed panel before that panel has its
  // final dimensions. Keep the initial conversation at the bottom as the
  // panel opens or the transcript reflows, unless the user has scrolled away.
  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (
      !pinTranscriptToBottom ||
      !hasMessages ||
      !scrollContainer ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (isPinnedToBottomRef.current) {
        scrollToBottom();
      }
    });

    resizeObserver.observe(scrollContainer);
    const transcriptContent = scrollContainer.firstElementChild;
    if (transcriptContent instanceof HTMLElement) {
      resizeObserver.observe(transcriptContent);
    }

    return () => resizeObserver.disconnect();
  }, [hasMessages, pinTranscriptToBottom, scrollToBottom]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const scrollContainer = event.currentTarget;
      const distanceFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;
      const isAtBottom = distanceFromBottom <= BOTTOM_SCROLL_THRESHOLD_PX;

      // Programmatic scrolling (for example, focusing a newly sent message)
      // must not opt out of pinning. Only direct scroll input can do that.
      if (isAtBottom || hasUserScrollIntentRef.current) {
        setPinnedToBottom(isAtBottom);
      }
      hasUserScrollIntentRef.current = false;
    },
    [setPinnedToBottom]
  );

  return (
    <Stack
      direction="column"
      sx={{
        height: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        width: '100%',
        ...sx,
      }}
    >
      {header}
      <Stack
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Stack
          ref={scrollContainerRef}
          aria-label="Chat transcript"
          role="region"
          tabIndex={0}
          onKeyDown={(event) => {
            if (
              [
                'ArrowDown',
                'ArrowUp',
                'End',
                'Home',
                'PageDown',
                'PageUp',
                ' ',
              ].includes(event.key)
            ) {
              hasUserScrollIntentRef.current = true;
            }
          }}
          onPointerCancel={() => {
            hasUserScrollIntentRef.current = false;
          }}
          onPointerDown={() => {
            hasUserScrollIntentRef.current = true;
          }}
          onPointerUp={() => {
            hasUserScrollIntentRef.current = false;
          }}
          onScroll={handleScroll}
          onTouchEnd={() => {
            hasUserScrollIntentRef.current = false;
          }}
          onTouchMove={() => {
            hasUserScrollIntentRef.current = true;
          }}
          onWheel={() => {
            hasUserScrollIntentRef.current = true;
          }}
          sx={{
            height: '100%',
            justifyContent: hasMessages ? 'flex-start' : 'center',
            overflow: 'auto',
          }}
        >
          {hasMessages ? transcript : emptyState}
        </Stack>
        {pinTranscriptToBottom && hasMessages && !isPinnedToBottom && (
          <IconButton
            ariaLabel="Scroll to latest message"
            icon={ArrowDownIcon}
            onClick={() => scrollToBottom('smooth')}
            size="md"
            sx={{
              position: 'absolute',
              right: '16px',
              bottom: '16px',
            }}
          />
        )}
      </Stack>
      {footer}
    </Stack>
  );
}
